// Dynamically load a Google Fonts family into the document <head>.
// Idempotent — no-ops if the font was already requested (checked by <link> id).
// Two links are used on purpose: the plain family always resolves if the font exists,
// while the weight/italic request may 400 for families without those styles (which
// used to make the whole font silently fall back to a system sans).
export function loadGoogleFont(family) {
  if (typeof document === "undefined" || !family || typeof family !== "string") return;
  const clean = family.trim();
  if (!clean) return;
  const slug = clean.replace(/\s+/g, "-").toLowerCase();
  const name = encodeURIComponent(clean).replace(/%20/g, "+");
  const add = (id, href) => {
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  };
  add(`gfont-${slug}`, `https://fonts.googleapis.com/css2?family=${name}&display=swap`);
  add(
    `gfont-${slug}-styles`,
    `https://fonts.googleapis.com/css2?family=${name}:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap`
  );
}

// Popular families used to suggest a correction when a typo is entered.
const POPULAR_FAMILIES = [
  "Montserrat", "Playfair Display", "Poppins", "Lato", "Roboto", "Open Sans", "Raleway", "Inter",
  "Nunito", "Quicksand", "Outfit", "Merriweather", "Lora", "Cormorant Garamond", "Libre Baskerville",
  "Dancing Script", "Great Vibes", "Parisienne", "Sacramento", "Pacifico", "Josefin Sans", "Karla",
  "Work Sans", "Rubik", "DM Sans", "Manrope", "Mulish", "Cardo", "EB Garamond", "Bodoni Moda",
  "Italiana", "Marcellus", "Tenor Sans", "Jost", "Figtree", "Sora", "Bebas Neue", "Oswald", "Anton",
];

function editDistance(a, b) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
  }
  return dp[m][n];
}

export function suggestFamily(family) {
  const q = (family || "").trim().toLowerCase();
  if (!q) return null;
  let best = null, bestScore = Infinity;
  for (const f of POPULAR_FAMILIES) {
    const d = editDistance(q, f.toLowerCase());
    if (d < bestScore) { bestScore = d; best = f; }
  }
  return bestScore <= Math.max(2, Math.round(q.length * 0.34)) ? best : null;
}

// Asks the backend whether Google Fonts serves this family and with which capitalization.
// Uses the backend because fonts.googleapis.com does not allow browser fetches (CORS).
// -> { available: boolean, resolved: string|null }
export async function resolveGoogleFont(family) {
  const q = (family || "").trim().replace(/\s+/g, " ");
  if (!q) return { available: false, resolved: null };
  try {
    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/fonts/check?family=${encodeURIComponent(q)}`
    );
    if (!res.ok) return { available: true, resolved: q };
    const data = await res.json();
    return { available: !!data.available, resolved: data.resolved || (data.available ? q : null) };
  } catch {
    return { available: true, resolved: q };
  }
}

export async function isGoogleFontAvailable(family) {
  return (await resolveGoogleFont(family)).available;
}

// Deduplicated merge of built-in font list + user-added custom fonts (filtered by category).
// Custom font schema: { id: string, label: string, family: string, category: "headline"|"script"|"body" }
export function mergeCustomFonts(builtInList, customFonts, category, defaultGeneric) {
  const customs = (customFonts || [])
    .filter((f) => f && f.family && f.category === category)
    .map((f) => ({
      id: f.id || f.family,
      label: f.label || f.family,
      css: `'${f.family}', ${defaultGeneric}`,
    }));
  // Prefer existing built-in first, then push customs not already in the list
  const seen = new Set(builtInList.map((f) => f.id.toLowerCase()));
  const merged = [...builtInList];
  for (const c of customs) {
    if (!seen.has(c.id.toLowerCase())) {
      merged.push(c);
      seen.add(c.id.toLowerCase());
    }
  }
  return merged;
}
